/**
 * Application-wide constants to eliminate magic numbers and hardcoded values
 */

// File handling constants
export const FILE_LIMITS = {
  MAX_SIZE: 10 * 1024 * 1024, // 10MB
  ACCEPTED_TYPES: '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.zip,.rar',
  DESCRIPTION: 'PDF, DOC, XLS, images, and archives up to 10MB'
} as const;

// Image processing constants
export const IMAGE_PROCESSING = {
  PROFILE_PICTURE: {
    TARGET_WIDTH: 512,
    TARGET_HEIGHT: 512,
    QUALITY: 0.9,
    MAX_FILE_SIZE: 5 * 1024 * 1024 // 5MB for profile pictures
  },
  THUMBNAIL: {
    TARGET_WIDTH: 128,
    TARGET_HEIGHT: 128,
    QUALITY: 0.8
  }
} as const;

// Pagination constants
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 25,
  PAGE_SIZE_OPTIONS: [25, 50, 100, 250] as const,
  MAX_PAGE_SIZE: 250
} as const;

// Timeout constants
export const TIMEOUTS = {
  EMERGENCY_LOADING: 5000, // 5 seconds
  AUTO_SAVE: 1000,         // 1 second
  DEBOUNCE_SEARCH: 300,    // 300ms
  NOTIFICATION_CLEAR: 2000, // 2 seconds
  SESSION_CHECK: 30000     // 30 seconds
} as const;

// Z-index constants for layering
export const Z_INDEX = {
  DROPDOWN: 10,
  MODAL: 50,
  NOTIFICATION: 100,
  TOOLTIP: 200
} as const;

// Animation durations (in milliseconds)
export const ANIMATION = {
  FAST: 150,
  NORMAL: 200,
  SLOW: 300,
  VERY_SLOW: 500
} as const;

// Form validation constants
export const VALIDATION = {
  MIN_PASSWORD_LENGTH: 6,
  MAX_USERNAME_LENGTH: 50,
  MAX_COMPANY_NAME_LENGTH: 100,
  MAX_DESCRIPTION_LENGTH: 500,
  MAX_MESSAGE_LENGTH: 1000
} as const;

// Financial data constants
export const FINANCIAL = {
  YEARS: [2025, 2024, 2023, 2022, 2021, 2020] as const,
  MONTHS: [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ] as const,
  CURRENCY_DECIMAL_PLACES: 2
} as const;

// Admin activity constants
export const ADMIN_ACTIVITY = {
  DEFAULT_ITEMS_PER_PAGE: 25,
  MAX_EXPORT_ITEMS: 10000,
  ACTIVITY_RETENTION_DAYS: 90
} as const;

// Marketplace constants
export const MARKETPLACE = {
  MIN_SHARE_PRICE: 0.01,
  MAX_SHARE_PRICE: 1000000,
  MIN_SHARE_QUANTITY: 1,
  MAX_SHARE_QUANTITY: 1000000
} as const;

// Meeting constants
export const MEETINGS = {
  MAX_ATTENDEES: 1000,
  DEFAULT_MEETING_DURATION: 60, // minutes
  REMINDER_DAYS_BEFORE: [7, 3, 1] as const
} as const;

// Notification constants
export const NOTIFICATIONS = {
  MAX_UNREAD_COUNT: 99,
  BATCH_SIZE: 50,
  AUTO_MARK_READ_DELAY: 3000, // 3 seconds
  POLLING_INTERVAL: 30000     // 30 seconds
} as const;

// Storage keys for localStorage/sessionStorage
export const STORAGE_KEYS = {
  USER_PREFERENCES: 'user_preferences',
  THEME: 'theme',
  SIDEBAR_COLLAPSED: 'sidebar_collapsed',
  LAST_VISITED_PAGE: 'last_visited_page'
} as const;

// Error messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FILE_TOO_LARGE: `File size exceeds ${FILE_LIMITS.MAX_SIZE / (1024 * 1024)}MB limit.`,
  INVALID_FILE_TYPE: 'Invalid file type. Please upload a supported file format.',
  GENERIC_ERROR: 'An unexpected error occurred. Please try again.'
} as const;

// Success messages
export const SUCCESS_MESSAGES = {
  SAVED: 'Changes saved successfully!',
  UPLOADED: 'File uploaded successfully!',
  DELETED: 'Item deleted successfully!',
  CREATED: 'Item created successfully!',
  UPDATED: 'Item updated successfully!'
} as const;

// API constants
export const API = {
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second base delay
  REQUEST_TIMEOUT: 30000 // 30 seconds
} as const;

// Date format constants
export const DATE_FORMATS = {
  DISPLAY: 'MMM dd, yyyy',
  INPUT: 'yyyy-MM-dd',
  FULL: 'EEEE, MMMM dd, yyyy',
  TIME: 'HH:mm',
  DATETIME: 'MMM dd, yyyy HH:mm'
} as const; 